/**
 * GlobalSearchComponent - A comprehensive search component for devices, clients, and features
 *
 * This component provides a unified search interface that allows users to search across:
 * - Network devices (routers, switches, access points, etc.)
 * - Connected clients
 * - Application features and settings
 * - Search history
 *
 * Features:
 * - Real-time search with debouncing
 * - Tabbed interface for different search categories
 * - Search history management
 * - Role-based feature access control
 * - Responsive design for mobile and desktop
 * - Material UI components with consistent theming
 *
 * @author Development Team
 * @version 1.0.0
 * @since 2024
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';

// Material UI components for consistent design system
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemButton from '@mui/material/ListItemButton';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import TruncateTooltip from '../../../../commonComponent/TruncateTooltip';
// Third-party libraries
import Cookies from 'universal-cookie';

// Project-specific imports
import { ThemeMode } from 'config';
import useApi from 'hooks/useApi';
import {
  resolveFeatureLink,
  filterFeatureSearchResults,
  FEATURE_SEARCH_MAX_RESULTS,
  isSsidScopeMatchingPage,
  isSchedulesScopeMatchingPage,
  isOrgWideSsidPage,
  isLocationSsidPage,
  isLocationEditableSsid,
  isAtLocationScope,
  isAtOrganizationScope,
  currentOrgHasLocationsFromHierarchy
} from './featureSearchData';
import { highlightText } from './highlightText';
import { normalizeMacSearchQuery, sanitizeHistorySearchInput } from './searchInput';
import { useCloudRestApiAccess } from 'contexts/CloudRestApiAccessContext';
import { useFeatureListLoaded } from 'utils/supportedFeatures';
import currentUserRole from 'utils/currentUserRole';

// Icon components from Tabler Icons
import { IconX } from '@tabler/icons-react';

// Utility functions
import {
  getErrMessage,
  redirectToDevice,
  GLOBAL_SEARCH_CLIENT_NAV_EVENT,
  GLOBAL_SEARCH_SSID_SELECT_EVENT,
  OPEN_GLOBAL_SEARCH_SSID_HANDOFF,
  GLOBAL_SEARCH_SSID_SHOW_PROMPT_EVENT,
  GLOBAL_SEARCH_MANUAL_OPEN_EVENT,
  readGlobalSearchSsidState,
  setGlobalSearchSsidHandoff
} from 'utils/CommonMethods';
import constants from 'utils/constants';
import { useIntl } from 'react-intl';
import { getLocalizedMessage } from '../../../../utils/localizationUtils';
import { useOrganization } from '../../../../contexts/OrganizationContext';

// Initialize cookies instance for session management
const cookies = new Cookies();

/**
 * TabPanel Component - Renders content for individual tabs
 *
 * This component provides accessible tab panel functionality with proper ARIA attributes.
 * Only renders content when the corresponding tab is active for performance optimization.
 *
 * @param {React.ReactNode} children - The content to display when tab is active
 * @param {number} value - Current active tab index
 * @param {number} index - This tab panel's index
 * @param {object} other - Additional props passed to the container div
 * @returns {JSX.Element} Tab panel component
 */
function TabPanel({ children, value, index, ...other }) {
  return (
    <div role="tabpanel" hidden={value !== index} id={`search-tabpanel-${index}`} aria-labelledby={`search-tab-${index}`} {...other}>
      {value === index && <Box sx={{ p: 2 }}>{children}</Box>}
    </div>
  );
}

// PropTypes for TabPanel component
TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired
};

/**
 * Primary label shown for a client row in global search (must match clients-list pre-fill).
 */
export const getGlobalSearchClientDisplayName = (client) =>
  (client?.clientName || client?.name || client?.Title || client?.deviceName || '').trim();

/** Primary label for a row in global search (feature titles vs device/client names). */
export const getGlobalSearchResultDisplayName = (item, type) => {
  if (type === 'feature') {
    return String(item?.localizedTitle || item?.Title || '').trim();
  }
  if (type === 'ssid') {
    return String(item?.Title || item?.ssidName || '').trim();
  }
  return getGlobalSearchClientDisplayName(item);
};

/** Collapsed feature rows shown before "Show more" (Network Settings has 7 sub-features). */
const FEATURE_SEARCH_COLLAPSED_LIMIT = 10;

/**
 * SearchResults Component - Displays search results for different categories
 *
 * This component handles the display of search results with loading states, error handling,
 * and search term highlighting. It supports different result types (devices, clients, features).
 *
 * @param {string} title - Display title for the results section
 * @param {Array} results - Array of search result objects
 * @param {boolean} loading - Loading state indicator
 * @param {string} error - Error message if search failed
 * @param {Function} onItemClick - Callback when user clicks on a result item
 * @param {string} searchQuery - Current search query for highlighting
 * @param {string} type - Type of results ('device', 'client', 'feature')
 * @param {boolean} expanded - Whether to show all results or just first 5
 * @param {Function} onToggleExpand - Callback to toggle expanded state
 * @returns {JSX.Element} Search results component
 */
function SearchResults({
  title,
  results,
  loading,
  error,
  onItemClick,
  searchQuery,
  type,
  expanded = false,
  onToggleExpand,
  getWarningMessage,
  initialDisplayLimit = 5
}) {
  const theme = useTheme();
  const intl = useIntl();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography variant="body1" color="grey.800" sx={{ mt: 1.5 }}>
        {error}
      </Typography>
    );
  }

  if (!results || results.length === 0) {
    return (
      <Typography variant="body1" color="grey.800" sx={{ mt: 1.5 }}>
        {getLocalizedMessage(intl, 'no')} {title.toLowerCase()} {getLocalizedMessage(intl, 'foundMatchingYourSearch')}
      </Typography>
    );
  }

  const displayResults = expanded ? results : results.slice(0, initialDisplayLimit);
  const hasMoreResults = results.length > initialDisplayLimit;

  return (
    <List dense sx={{ maxHeight: expanded ? 400 : 200, overflow: 'auto' }}>
      {displayResults.map((item, displayIndex) => {
        // Find the actual index in the full results array
        const actualIndex = results.findIndex(
          (r) =>
            (r.Title && item.Title && r.Title === item.Title && (!r.link || !item.link || r.link === item.link)) ||
            (r.name && item.name && r.name === item.name) ||
            (r.deviceName && item.deviceName && r.deviceName === item.deviceName) ||
            (r.id && item.id && r.id === item.id)
        );
        const itemIndex = actualIndex !== -1 ? actualIndex : displayIndex;

        return (
          <ListItem key={itemIndex} disablePadding>
            <ListItemButton
              onClick={() => onItemClick(item, type, itemIndex)}
              sx={{
                px: 0,
                py: '2px',
                '&:hover': {
                  backgroundColor: 'transparent' // removes default hover background
                }
              }}
            >
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <TruncateTooltip
                      sx={{
                        color: 'grey.800',
                        maxWidth: '200px'
                      }}
                    >
                      {highlightText(getGlobalSearchResultDisplayName(item, type) || getLocalizedMessage(intl, 'naCommon'), searchQuery)}
                    </TruncateTooltip>
                    {(item.orgName || item.networkName) && (
                      <TruncateTooltip
                        sx={{
                          maxWidth: '300px',
                          color: theme.palette.mode === ThemeMode.DARK ? 'color.default' : '#78909C'
                        }}
                      >
                        ({item.deviceType && `${item.deviceType} ${getLocalizedMessage(intl, 'at')} `}
                        {item.orgName && `${item.orgName} / `}
                        {item.networkName})
                      </TruncateTooltip>
                    )}
                  </Box>
                }
                secondary={
                  item.showWarning &&
                  getWarningMessage &&
                  getWarningMessage(item) && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                      {getWarningMessage(item)}
                    </Typography>
                  )
                }
              />
            </ListItemButton>
          </ListItem>
        );
      })}
      {hasMoreResults && (
        <ListItem sx={{ px: 0, py: 0 }}>
          <Typography
            variant="body2"
            onClick={() => onToggleExpand && onToggleExpand(type)}
            sx={{
              cursor: 'pointer',
              color: theme.palette.mode === ThemeMode.DARK ? 'color.default' : 'teal.900',
              textAlign: 'center',
              width: '100%',
              py: 1,
              '&:hover': {
                textDecoration: 'underline'
              }
            }}
          >
            {expanded
              ? `${getLocalizedMessage(intl, 'showLess')} ${title}`
              : `${results.length - initialDisplayLimit} ${getLocalizedMessage(intl, 'more')} ${title}`}
          </Typography>
        </ListItem>
      )}
    </List>
  );
}

SearchResults.propTypes = {
  title: PropTypes.string.isRequired,
  results: PropTypes.array,
  loading: PropTypes.bool,
  error: PropTypes.string,
  onItemClick: PropTypes.func.isRequired,
  searchQuery: PropTypes.string,
  type: PropTypes.string.isRequired,
  expanded: PropTypes.bool,
  onToggleExpand: PropTypes.func,
  getWarningMessage: PropTypes.func,
  onOpenOrgTree: PropTypes.func
};

// Search History Component
function SearchHistory({ history, onHistoryClick, onHistoryDelete, onClearAll }) {
  const theme = useTheme();
  const intl = useIntl();

  if (!history || history.length === 0) {
    return (
      <Typography variant="body2" color="grey.800" sx={{ p: 2, textAlign: 'center' }}>
        {getLocalizedMessage(intl, 'noRecentSearchHistory')}.
      </Typography>
    );
  }

  return (
    <Box
      sx={{
        px: 2.5,
        py: 2,
        overflow: 'auto',
        maxHeight: 'calc(100vh - 215px)',
        '&::-webkit-scrollbar': { width: 3 }
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
        <Typography component="p" variant="caption" color="grey.600">
          {getLocalizedMessage(intl, 'history')}
        </Typography>
        <Typography
          variant="body1"
          onClick={onClearAll}
          sx={{
            cursor: 'pointer',
            color: theme.palette.mode === ThemeMode.DARK ? 'color.default' : 'teal.900'
          }}
        >
          {getLocalizedMessage(intl, 'clearAll')}
        </Typography>
      </Box>
      <List dense>
        {history.map((item, index) => (
          <ListItem key={index} disablePadding>
            <ListItemButton
              onClick={() => onHistoryClick(item.searchValue)}
              sx={{
                px: 0,
                py: '2px',
                '&:hover': {
                  backgroundColor: 'transparent' // removes default hover background
                }
              }}
            >
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <TruncateTooltip sx={{ color: 'grey.800', maxWidth: '300px' }}>{item.searchValue}</TruncateTooltip>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onHistoryDelete(item.searchValue);
                      }}
                      sx={{ ml: 1 }}
                    >
                      <IconX size="16px" />
                    </IconButton>
                  </Box>
                }
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );
}

SearchHistory.propTypes = {
  history: PropTypes.array,
  onHistoryClick: PropTypes.func.isRequired,
  onHistoryDelete: PropTypes.func.isRequired,
  onClearAll: PropTypes.func.isRequired
};

/** Router base name from <base href> or the build's BASE_URL ('' at the root). */
const getRouterBaseName = () => {
  if (typeof document !== 'undefined') {
    const baseElement = document.querySelector('base');
    const baseHref = baseElement?.getAttribute('href');
    if (baseHref) {
      const trimmed = baseHref.endsWith('/') && baseHref !== '/' ? baseHref.slice(0, -1) : baseHref;
      return trimmed === '/' ? '' : trimmed;
    }
  }
  const base = import.meta.env.BASE_URL || '/';
  return base === '/' ? '' : base.replace(/\/$/, '');
};

/**
 * GlobalSearchComponent - Main search component with comprehensive functionality
 *
 * This is the primary component that orchestrates all search functionality including:
 * - Device search across network infrastructure
 * - Client search for connected users
 * - Feature search for application functionality
 * - Search history management with CRUD operations
 * - Role-based access control for features
 * - Responsive design adaptation
 *
 * @param {string} searchQuery - Current search query string
 * @param {Function} onClose - Callback to close the search dropdown
 * @param {Function} onSearchChange - Callback when search query changes
 * @param {boolean} isMobile - Flag indicating if component is in mobile mode
 * @param {boolean} isDropdown - Flag indicating if component is in dropdown mode
 * @returns {JSX.Element} The complete global search component
 */
const GlobalSearchComponent = ({ searchQuery, onClose, onSearchChange }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const intl = useIntl();
  const { updateOrganizationContext, refreshHierarchyTree } = useOrganization();
  // Tracks whether the supported-feature flags (`featureSupportAdminCheck`) are
  // still being fetched by CloudRestApiAccessProvider. Mirrors the gate used by
  // NavItem so search results stay consistent with the sidebar.
  const { loading: featureFlagsLoading, enabled: cloudRestEnabled } = useCloudRestApiAccess();
  const featureListLoaded = useFeatureListLoaded();
  // Ref shadows so `searchFeatures` can keep stable [] deps but still see the
  // latest loading / access values when the main search effect invokes it.
  const featureFlagsLoadingRef = useRef(featureFlagsLoading);
  const cloudRestEnabledRef = useRef(cloudRestEnabled);
  const featureListLoadedRef = useRef(featureListLoaded);
  const intlRef = useRef(intl);
  useEffect(() => {
    featureFlagsLoadingRef.current = featureFlagsLoading;
    cloudRestEnabledRef.current = cloudRestEnabled;
    featureListLoadedRef.current = featureListLoaded;
    intlRef.current = intl;
  }, [featureFlagsLoading, cloudRestEnabled, featureListLoaded, intl]);

  // State management for active tab in the search interface
  const [activeTab, setActiveTab] = useState(0);

  // Search results state for different categories
  const [deviceResults, setDeviceResults] = useState([]);
  const [clientResults, setClientResults] = useState([]);
  const [featureResults, setFeatureResults] = useState([]);
  const [ssidResults, setSsidResults] = useState([]);
  const [searchHistory, setSearchHistory] = useState([]);

  // Expanded states for result sections
  const [expandedSections, setExpandedSections] = useState({
    device: false,
    client: false,
    feature: false,
    ssid: false
  });

  // Loading states for different search operations
  const [loading, setLoading] = useState({
    devices: false,
    clients: false,
    features: false,
    history: false,
    ssids: false
  });

  // Error states for different search operations
  const [errors, setErrors] = useState({
    devices: '',
    clients: '',
    features: '',
    history: '',
    ssids: ''
  });

  const isSsidPage = /\/SSID\/?$/i.test(location.pathname) || location.pathname.includes('/SSID');
  const isOrgSsidPage = location.pathname.includes('/organization/') && location.pathname.includes('/SSID');
  const isLocationLevelSsidPage = isLocationSsidPage(location.pathname);
  const [ssidHandoffVersion, setSsidHandoffVersion] = useState(0);
  const ssidHandoffState = useMemo(() => {
    void ssidHandoffVersion;
    return readGlobalSearchSsidState();
  }, [ssidHandoffVersion]);
  const ssidPickerMode = isSsidPage && ssidHandoffState.isSsidPicker;

  useEffect(() => {
    const onSsidHandoff = () => setSsidHandoffVersion((version) => version + 1);
    const onManualOpen = () => {
      setSsidHandoffVersion((version) => version + 1);
      setSsidResults([]);
      setDeviceResults([]);
      setClientResults([]);
      setFeatureResults([]);
      setActiveTab(0);
      setExpandedSections({
        device: false,
        client: false,
        feature: false,
        ssid: false
      });
    };

    window.addEventListener(OPEN_GLOBAL_SEARCH_SSID_HANDOFF, onSsidHandoff);
    window.addEventListener(GLOBAL_SEARCH_MANUAL_OPEN_EVENT, onManualOpen);
    return () => {
      window.removeEventListener(OPEN_GLOBAL_SEARCH_SSID_HANDOFF, onSsidHandoff);
      window.removeEventListener(GLOBAL_SEARCH_MANUAL_OPEN_EVENT, onManualOpen);
    };
  }, []);

  useEffect(() => {
    if (ssidPickerMode) {
      setActiveTab(0);
    }
  }, [ssidPickerMode]);

  // Organization and location availability flags
  /**
   * Fetch search history from the server
   *
   * Retrieves the user's search history for display when no active search query exists.
   * Uses memoization with useCallback for performance optimization.
   *
   * @async
   * @function fetchSearchHistory
   * @returns {Promise<void>} Promise that resolves when history is fetched
   */
  const fetchSearchHistory = useCallback(async () => {
    // Only fetch history when there's no active search query
    if (!searchQuery) {
      setLoading((prev) => ({ ...prev, history: true }));
      try {
        const response = await useApi('FETCH_GLOBAL_SEARCH_HISTORY');
        if (response?.response?.status) {
          setSearchHistory(response.details?.searchHistory || []);
        }
      } catch (error) {
        console.error('Error fetching search history:', error);
        setErrors((prev) => ({ ...prev, history: 'Failed to load search history' }));
      }
      setLoading((prev) => ({ ...prev, history: false }));
    }
  }, [searchQuery]);

  // Fetch device search results
  const fetchDeviceResults = useCallback(async (query) => {
    if (!query) return;

    setLoading((prev) => ({ ...prev, devices: true }));
    setErrors((prev) => ({ ...prev, devices: '' }));

    try {
      const response = await useApi('FETCH_GLOBAL_SEARCH_DATA', {
        searchType: 'globalDeviceSearch',
        searchQuery: query.trim()
      });

      if (response?.response?.status) {
        setDeviceResults(response.details?.deviceDetail || []);
      } else {
        setDeviceResults([]); // Clear results on API error
        setErrors((prev) => ({ ...prev, devices: getErrMessage(response?.response?.responseCode) }));
      }
    } catch (error) {
      setDeviceResults([]); // Clear results on exception
      setErrors((prev) => ({ ...prev, devices: 'Error fetching device results' }));
    }

    setLoading((prev) => ({ ...prev, devices: false }));
  }, []);

  // Fetch client search results
  const fetchClientResults = useCallback(
    async (query) => {
      if (!query) return;

      setLoading((prev) => ({ ...prev, clients: true }));
      setErrors((prev) => ({ ...prev, clients: '' }));

      try {
        const response = await useApi('FETCH_GLOBAL_SEARCH_DATA', {
          searchType: 'globalClientSearch',
          searchQuery: query.trim()
        });

        if (response?.response?.status) {
          setClientResults(response.details?.clientDetails || []);
        } else {
          setClientResults([]); // Clear results on API error
          const responseCode = response?.response?.responseCode;
          const serviceUnavailable = Number(responseCode) === 60009;
          const serviceUnavailableMessage = getErrMessage(responseCode);

          setErrors((prev) => ({
            ...prev,
            clients: serviceUnavailable ? `${getLocalizedMessage(intl, 'noClientMatchMsg')}` : serviceUnavailableMessage
          }));
        }
      } catch (error) {
        setClientResults([]); // Clear results on exception
        setErrors((prev) => ({ ...prev, clients: 'Error fetching client results' }));
      }

      setLoading((prev) => ({ ...prev, clients: false }));
    },
    [intl]
  );

  // Search features — recomputed when query or feature-flag hydration changes
  const runFeatureSearch = useCallback((query) => {
    localStorage.removeItem('redirectedFrom');

    if (!query?.trim()) {
      setFeatureResults([]);
      setLoading((prev) => ({ ...prev, features: false }));
      return;
    }

    setLoading((prev) => ({ ...prev, features: true }));

    const menuVisibilityGates = {
      featureFlagsLoading: featureFlagsLoadingRef.current || !featureListLoadedRef.current,
      cloudRestLoading: featureFlagsLoadingRef.current,
      cloudRestEnabled: cloudRestEnabledRef.current,
      userRole: currentUserRole()
    };

    setFeatureResults(filterFeatureSearchResults(query, menuVisibilityGates, intlRef.current));
    setLoading((prev) => ({ ...prev, features: false }));
  }, []);

  const fetchSsidResults = useCallback(
    async (query) => {
      setLoading((prev) => ({ ...prev, ssids: true }));
      setErrors((prev) => ({ ...prev, ssids: '' }));

      try {
        const orgId = localStorage.getItem('OrgId') || '';
        const handoff = readGlobalSearchSsidState();
        const normalizedQuery = (query || '').trim().toLowerCase();
        const handoffTitle = (handoff.query || '').trim().toLowerCase();
        const filterText = normalizedQuery && normalizedQuery !== handoffTitle ? normalizedQuery : '';

        if (isOrgSsidPage) {
          const response = await useApi('GET_SSID_INFO_ORG', { orgId });
          const wirelessNetworks = response?.details?.wirelessOrgList || [];
          setSsidResults(
            wirelessNetworks
              .map((ssidData) => ({
                id: ssidData.wirelessOrgId,
                Title: ssidData.ssid || ssidData.wirelessNetworkName || '',
                ssidName: ssidData.ssid || ssidData.wirelessNetworkName || ''
              }))
              .filter((item) => !filterText || String(item.Title).toLowerCase().includes(filterText))
          );
        } else {
          const selectedTypeRaw = localStorage.getItem('selectedType');
          const networkId = selectedTypeRaw
            ? JSON.parse(selectedTypeRaw).id
            : localStorage.getItem('currentNetwork') || localStorage.getItem('networkId') || '';
          const response = await useApi('GET_SSID_INFO_LOCATION', { networkId });
          const wirelessNetworks = response?.wirelessNetworks || [];
          setSsidResults(
            wirelessNetworks
              .filter((ssidData) => !isLocationLevelSsidPage || isLocationEditableSsid(ssidData))
              .map((ssidData) => ({
                id: ssidData.wirelessNetworkId,
                Title: ssidData.wirelessNetworkName || ssidData.ssid || '',
                ssidName: ssidData.wirelessNetworkName || ssidData.ssid || ''
              }))
              .filter((item) => !filterText || String(item.Title).toLowerCase().includes(filterText))
          );
        }
      } catch (error) {
        setSsidResults([]);
        setErrors((prev) => ({ ...prev, ssids: 'Error fetching SSID results' }));
      }

      setLoading((prev) => ({ ...prev, ssids: false }));
    },
    [isOrgSsidPage, isLocationLevelSsidPage]
  );

  const visibleFeatureResults =
    featureResults.length > FEATURE_SEARCH_MAX_RESULTS ? featureResults.slice(0, FEATURE_SEARCH_MAX_RESULTS) : featureResults;
  const featureResultsTruncated = featureResults.length > FEATURE_SEARCH_MAX_RESULTS;

  // Handle search (devices/clients API + features; debounced on query changes)
  useEffect(() => {
    if (ssidPickerMode) {
      const query = (searchQuery || readGlobalSearchSsidState().query || '').trim();
      setExpandedSections({
        device: false,
        client: false,
        feature: false,
        ssid: true
      });

      const timeoutId = setTimeout(() => {
        fetchSsidResults(query);
      }, 300);

      return () => clearTimeout(timeoutId);
    }

    if (searchQuery && searchQuery.trim()) {
      const query = searchQuery.trim();
      const normalizedQuery = normalizeMacSearchQuery(query);

      // Reset expanded sections when starting a new search
      setExpandedSections({
        device: false,
        client: false,
        feature: false,
        ssid: false
      });

      // Debounce search
      const timeoutId = setTimeout(() => {
        fetchDeviceResults(normalizedQuery);
        fetchClientResults(normalizedQuery);
        runFeatureSearch(query);
      }, 500);

      return () => clearTimeout(timeoutId);
    } else {
      setDeviceResults([]);
      setClientResults([]);
      setFeatureResults([]);
      setSsidResults([]);
      setExpandedSections({
        device: false,
        client: false,
        feature: false,
        ssid: false
      });
      fetchSearchHistory();
    }
  }, [searchQuery, fetchDeviceResults, fetchClientResults, runFeatureSearch, fetchSearchHistory, ssidPickerMode, fetchSsidResults]);

  // Re-filter feature results when supported-feature flags finish loading, or when the UI language
  // changes: results carry a localized title and are matched against it (no device/client refetch).
  // runFeatureSearch reads intl from intlRef; its sync effect is declared earlier, so it has already run.
  const uiLocale = intl.locale;
  useEffect(() => {
    if (!searchQuery?.trim() || featureFlagsLoading || !featureListLoaded) return;
    runFeatureSearch(searchQuery.trim());
  }, [featureFlagsLoading, featureListLoaded, cloudRestEnabled, searchQuery, runFeatureSearch, uiLocale]);

  // No mount effect for history: the search effect above already runs on mount and takes its
  // empty-query branch, which fetches the history. A second effect here duplicated that call on
  // every open, and — because fetchSearchHistory is rebuilt whenever searchQuery changes — re-ran
  // on every keystroke as well, relying on the callback's internal guard to do nothing.

  // Utility function to navigate properly based on routing context
  const navigateToPath = useCallback(
    (targetPath, shouldReload = false) => {
      if (!targetPath) return;

      let normalizedPath = targetPath.trim();

      // Allow absolute URLs (safety for any external links)
      if (/^https?:\/\//i.test(normalizedPath)) {
        window.location.href = normalizedPath;
        return;
      }

      // Normalize hash/relative paths
      if (normalizedPath.startsWith('#')) {
        normalizedPath = normalizedPath.substring(1);
      }
      if (!normalizedPath.startsWith('/')) {
        normalizedPath = `/${normalizedPath}`;
      }

      const baseName = getRouterBaseName();
      if (baseName && normalizedPath.startsWith(`${baseName}/`)) {
        normalizedPath = normalizedPath.substring(baseName.length);
        if (!normalizedPath.startsWith('/')) {
          normalizedPath = `/${normalizedPath}`;
        }
      }

      const currentPath = window.location.pathname || '';
      const currentSearch = window.location.search || '';
      const currentHash = window.location.hash || '';
      const queryIndex = normalizedPath.indexOf('?');
      const targetPathname = queryIndex >= 0 ? normalizedPath.slice(0, queryIndex) : normalizedPath;
      const targetSearch = queryIndex >= 0 ? normalizedPath.slice(queryIndex) : '';
      const fullTargetPathname = `${baseName || ''}${targetPathname}`;

      if (shouldReload) {
        const normalizedCurrentPath = currentPath.endsWith('/') && fullTargetPathname !== '/' ? currentPath.slice(0, -1) : currentPath;
        if ((normalizedCurrentPath === fullTargetPathname && currentSearch === targetSearch) || currentHash === `#${normalizedPath}`) {
          window.location.reload();
          return;
        }
      }

      if (typeof navigate === 'function') {
        navigate({ pathname: targetPathname, search: targetSearch });
        return;
      }

      // Fallback for contexts without React Router (legacy hash pages)
      window.location.href = `${targetPathname}${targetSearch}`;
    },
    [navigate]
  );

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    // Reset expanded sections when switching back to All tab (index 0)
    // This ensures that All tab always shows only 5 items per section
    if (newValue === 0) {
      setExpandedSections({
        device: false,
        client: false,
        feature: false
      });
    }
  };

  // Handle expanding/collapsing result sections
  const handleToggleExpand = (type) => {
    // If we're on the "All" tab (index 0) and clicking "More", redirect to specific tab
    if (activeTab === 0) {
      // Map result types to their corresponding tab indices
      const tabMap = {
        device: 1,
        client: 2,
        feature: 3,
        ssid: 4
      };

      // Switch to the specific tab and expand that section
      setActiveTab(tabMap[type]);
      setExpandedSections((prev) => ({
        ...prev,
        [type]: true
      }));
    } else {
      // Normal toggle behavior when already on specific tabs
      setExpandedSections((prev) => ({
        ...prev,
        [type]: !prev[type]
      }));
    }
  };

  const handleSsidPickerClick = (ssidItem) => {
    if (ssidItem?.id == null || ssidItem.id === '') return;
    window.dispatchEvent(
      new CustomEvent(GLOBAL_SEARCH_SSID_SELECT_EVENT, {
        detail: {
          ssidId: ssidItem.id,
          ssidName: ssidItem.ssidName || ssidItem.Title || ''
        }
      })
    );
    setTimeout(() => onClose?.(), 0);
  };

  // Handle item click
  const handleItemClick = (item, type, index) => {
    switch (type) {
      case 'device':
        handleDeviceClick(item);
        break;
      case 'client':
        handleClientClick(item);
        break;
      case 'feature':
        handleFeatureClick(item, index);
        break;
      case 'ssid':
        handleSsidPickerClick(item);
        break;
      default:
        break;
    }
  };

  /**
   * Set parameters function - Critical function for device/client navigation
   *
   * This function replicates the exact behavior of the original globalSearch.js implementation.
   * It sets all necessary cookies and localStorage values before navigating to device or client pages.
   * This ensures proper context preservation when users navigate from search results.
   *
   * @param {Object} item - The search result item containing device/client information
   * @param {string} item.orgId - Organization ID
   * @param {string} item.orgName - Organization name
   * @param {string} item.orgLogo - Organization logo URL
   * @param {string} item.networkId - Network/location ID
   * @param {number} item.totalOnline - Number of online devices
   * @param {number} item.totalDevices - Total device count
   * @param {string} item.networkName - Network/location name
   * @param {string} item.wirelessRegion - Wireless region setting
   * @param {string} tab - The navigation context ('devices' or 'clients')
   */
  const setParameters = (item, tab) => {
    // Determine cookie security setting based on protocol and environment
    const secureCoockie = window.location.protocol === 'https:' || process.env.NODE_ENV === 'production';

    // Set organization context in cookies (secure for production)
    cookies.set('organization_Id', item.orgId, { secure: secureCoockie });
    localStorage.setItem('OrgId', item.orgId);
    localStorage.setItem('currentOrgName', item.orgName);
    localStorage.setItem('savedOrgName', item.orgName);
    localStorage.setItem('currentOrgLogo', item.orgLogo);
    localStorage.setItem('networkId', item.networkId);

    // Set network/location context in cookies and localStorage
    cookies.set('currentNetwork', item.networkId, { secure: secureCoockie });
    localStorage.setItem('currentNetwork', item.networkId);
    localStorage.setItem('totalOnline', item.totalOnline);
    localStorage.setItem('totalDevice', item.totalDevices);
    localStorage.setItem('currentNetworkName', item.networkName);
    localStorage.setItem('savedLocationName', item.networkName);
    localStorage.setItem('currentWirelessRegion', item.wirelessRegion);

    // Set selectedType for navigation menu to show correct organization/location selection
    // Structure: { id: networkId, label: networkName, type: "Loc" }
    const selectedType = {
      id: item.networkId,
      label: item.networkName,
      type: 'Loc',
      icon: 'Loc'
    };
    localStorage.setItem('selectedType', JSON.stringify(selectedType));

    // Set scopeId to 2 for location level
    localStorage.setItem('scopeId', '2');

    // Keep OrganizationContext in sync with localStorage values so dashboard summary
    // widgets use the right scope/org/network immediately after global-search navigation.
    updateOrganizationContext(item.orgId || '', '2', item.networkId || '', selectedType);
    refreshHierarchyTree();

    // Close the search dropdown to improve UX
    onClose();

    if (tab === 'devices') {
      const deviceId = item.deviceId || item.id || '';
      const deviceSerial = item.serialNo || '';
      const deviceType = item.deviceType || '';
      const deviceName = item.name || '';
      const deviceModel = item.model || '';
      const remoteMgmt = item.remoteManagement ?? item.remoteMgmtSt;
      const isMuiContext = !!window?.__INSIGHT_MUI__;
      const isUnmanaged = remoteMgmt == '0' || remoteMgmt == '2' || item.priStatus == 2;

      // Unmanaged devices: open the location device list (context already set above).
      if (isMuiContext && isUnmanaged) {
        navigateToPath('/locationDevice');
        return;
      }

      const selectedDevice = {
        id: deviceId,
        // The PR IPv6 gates (usePrFirewallParityGate and the other parity hooks) accept the SELECTED_DEVICE row only
        // when row.deviceId matches the selected device, and read the firmware from it: without both, a Pro Router
        // opened from global search gets the legacy pages.
        deviceId,
        fwVersion: item.fwVersion ?? item.firmwareVersion ?? undefined,
        serialNo: deviceSerial,
        name: deviceName,
        model: deviceModel,
        status: item.priStatus ?? item.status,
        networkId: item.networkId,
        orgId: item.orgId,
        deviceType,
        priStatus: item.priStatus,
        remoteManagement: remoteMgmt
      };

      localStorage.setItem(constants.SELECTED_DEVICE, JSON.stringify(selectedDevice));
      if (deviceId) {
        localStorage.setItem(constants.SELECTED_DEVICE_ID, deviceId);
      }
      localStorage.setItem('selectedDeviceName', deviceName);
      localStorage.setItem('selectedDeviceSerial', deviceSerial);
      localStorage.setItem('selectedDeviceType', deviceType);
      localStorage.setItem('deviceName', deviceName);
      localStorage.setItem('deviceModel', deviceModel);
      localStorage.setItem('organizationId', item.orgId || '');
      localStorage.setItem(
        'dashboardNewDeviceInfo',
        JSON.stringify({
          model: deviceModel,
          serial: deviceSerial,
          name: deviceName
        })
      );
      localStorage.setItem('isDnsSupport', '0');
      localStorage.setItem('isSpeedTestSupport', '0');

      const isDeviceManaged = remoteMgmt != '0' && remoteMgmt != '2' && item.priStatus !== 2;
      const muiDevicePath = (() => {
        if (!isMuiContext || !deviceType || !isDeviceManaged) return null;
        // All device types now follow the unified route pattern
        return `devices/${deviceType}/summary`;
      })();

      if (muiDevicePath) {
        navigateToPath(muiDevicePath);
        return;
      }

      // Navigate to legacy device page using utility function with proper parameters
      redirectToDevice(
        deviceName, // Device name
        deviceSerial, // Serial number
        deviceId, // Device ID
        deviceType, // Device type (AP, SW, BR, etc.)
        deviceModel, // Device model
        '', // Redirect type (empty for normal navigation)
        item.priStatus, // Primary status
        remoteMgmt // Remote management status
      );
    } else if (tab === 'clients') {
      // Reference: PRJCBUGEN-52396 - Global search client tracking
      localStorage.setItem('fromGlobalSearchComponent', '1');
      localStorage.setItem('redirectedFrom', 'globalSearch');

      const clientsPath = '/clients';
      const baseName = getRouterBaseName();
      const fullPathWithBase = `${baseName || ''}${clientsPath}`.replace(/\/+/g, '/');
      const currentPath = (window.location.pathname || '').replace(/\/$/, '') || '/';
      const normalizedTarget = fullPathWithBase.replace(/\/$/, '') || '/';
      const alreadyOnLocationClients = currentPath === normalizedTarget;

      if (alreadyOnLocationClients) {
        // Only the location-level /clients page should handle this in place (not org-level Clients)
        window.dispatchEvent(new CustomEvent(GLOBAL_SEARCH_CLIENT_NAV_EVENT));
      } else {
        navigateToPath(clientsPath, false);
      }
    }
  };

  // Handle device click - simplified to use setParameters
  const handleDeviceClick = (device) => {
    setParameters(device, 'devices');
  };

  // Handle client click - simplified to use setParameters
  const handleClientClick = (client) => {
    // Use only the label shown in the search result — never the user's global search query (IP/MAC/etc.)
    const query = getGlobalSearchClientDisplayName(client);
    if (query) {
      localStorage.setItem('globalSearchQuery', query);
    } else {
      localStorage.removeItem('globalSearchQuery');
    }
    setParameters(client, 'clients');
  };

  // Update feature list with warning status (matching legacy behavior)
  const updateListWithWarning = useCallback((prevList, index, warningStatus, warningType = null) => {
    const updatedList = [...prevList];
    if (updatedList[index]) {
      updatedList[index] = {
        ...updatedList[index],
        showWarning: warningStatus,
        warningType
      };
    }
    return updatedList;
  }, []);

  // Set feature level timer to show warning for 5 seconds (matching legacy behavior)
  const setFeatureLevelTimer = useCallback(
    (index, warningType = null) => {
      setFeatureResults((prevList) => updateListWithWarning(prevList, index, true, warningType));

      // Clear warning after 5 seconds (matching legacy behavior)
      setTimeout(() => {
        setFeatureResults((prevList) => updateListWithWarning(prevList, index, false, null));
      }, 5000);
    },
    [updateListWithWarning]
  );

  // Get user role (implement based on your auth system)
  const getUserRole = useCallback(() => {
    try {
      // Try to get the role from cookies (matching legacy behavior)
      const userRole = cookies.get('userRole');
      if (!userRole) return 'generic';

      // If role is already a string, return it
      if (typeof userRole === 'string') {
        // Encrypted roles can't be decrypted here; treat as unknown instead of generic
        if (userRole.includes('U2FsdGVkX1')) {
          return 'unknown';
        }
        return userRole;
      }

      // Otherwise return unknown as fallback
      return 'unknown';
    } catch (error) {
      return 'unknown';
    }
  }, []);

  // Check if feature is organization level restricted (matching legacy behavior)
  const isOrganizationLevelFeature = useCallback(() => {
    const currentPath = location.pathname;
    const currentHash = window.location.hash;

    // Check if user is at account/organization level pages
    if (
      currentPath.includes('mspHome') ||
      currentPath.includes('accountManagement') ||
      currentPath.includes('organization/createNewOrganization') ||
      currentPath.includes('organization/dashboard') ||
      currentPath.includes('dashboard/account') ||
      currentPath.includes('dashboard/myDevices') ||
      currentPath.includes('organization/devices') ||
      currentPath.includes('/reports/list') ||
      currentPath.includes('organization/settings') ||
      currentPath.includes('/dashboard/generateToken') ||
      currentPath.includes('notifications/all') ||
      currentPath.includes('manager/detail') ||
      currentHash.includes('accountManagement') ||
      currentHash.includes('organization/createNewOrganization') ||
      currentHash.includes('organization/dashboard') ||
      currentHash.includes('dashboard/account') ||
      currentHash.includes('dashboard/myDevices') ||
      currentHash.includes('organization/devices') ||
      currentHash.includes('/reports/list') ||
      currentHash.includes('organization/settings') ||
      currentHash.includes('/dashboard/generateToken') ||
      currentHash.includes('notifications/all') ||
      currentHash.includes('manager/detail')
    ) {
      return true;
    }

    return false;
  }, [location.pathname]);

  const openSsidPickerOnCurrentPage = useCallback(
    (feature) => {
      const isOrganizationScope = isOrgWideSsidPage(location.pathname);
      const featureLabel = feature.subFeatureName || feature.Title || '';
      setGlobalSearchSsidHandoff({
        featureTitle: feature.Title,
        subFeatureName: feature.subFeatureName || feature.Title || '',
        ssidScope: isOrganizationScope ? 'organization' : 'network',
        showPagePrompt: false
      });
      window.dispatchEvent(
        new CustomEvent(GLOBAL_SEARCH_SSID_SHOW_PROMPT_EVENT, {
          detail: { feature: featureLabel }
        })
      );
    },
    [location.pathname]
  );

  const openSsidPageWithHandoff = useCallback(
    (feature, ssidScope, targetLink) => {
      setGlobalSearchSsidHandoff({
        featureTitle: feature.Title,
        subFeatureName: feature.subFeatureName || feature.Title || '',
        ssidScope,
        showPagePrompt: true
      });
      navigateToPath(targetLink, false);
    },
    [navigateToPath]
  );

  const fetchCurrentOrgHasLocations = useCallback(async () => {
    const orgId = localStorage.getItem('OrgId');
    if (!orgId) return true;

    try {
      const result = await useApi('GET_ORG_CHILD_DETAILS', {}, {}, ['_Id', 'networkId']);
      if (!result?.response?.status) return true;
      return currentOrgHasLocationsFromHierarchy(result?.info || [], orgId);
    } catch {
      return true;
    }
  }, []);

  // Org-or-location tree for features with organizationLink/locationLink (SSID sub-features, Schedules).
  // Links stay raw: the tree resolves {orgName} from the organization the user picks.
  const openHierarchyTreeForScopedFeature = useCallback((feature) => {
    const treePromptLabel = feature.treePromptLabel || feature.Title;
    window.dispatchEvent(
      new CustomEvent('OPEN_ORG_TREE_FROM_SEARCH', {
        detail: {
          featureTitle: treePromptLabel,
          selectionType: 'organizationOrLocation',
          scopedTargetUrls: {
            organization: feature.organizationLink || feature.link,
            location: feature.locationLink || feature.link
          },
          ssidHandoff: feature.ssidSubFeature
            ? {
                featureTitle: feature.Title,
                subFeatureName: feature.subFeatureName || feature.Title || ''
              }
            : null
        }
      })
    );
  }, []);

  // Open a scoped feature's page directly; SSID sub-features also hand off to the SSID picker.
  const openScopedFeaturePage = useCallback(
    (feature, scope, targetLink) => {
      if (feature.ssidSubFeature) {
        openSsidPageWithHandoff(feature, scope, targetLink);
      } else {
        navigateToPath(targetLink, false);
      }
    },
    [openSsidPageWithHandoff, navigateToPath]
  );

  // Handle feature click with warning logic (matching legacy behavior)
  const handleFeatureClick = useCallback(
    async (feature, index) => {
      const userRole = getUserRole();
      const currentPath = location.pathname;
      const restrictedRoles = ['generic', 'preVoucherAdmin', 'supportAgent'];
      const lacksPremiumAccess = feature.premiumFeature && restrictedRoles.includes(userRole);
      const targetLink = resolveFeatureLink(feature.link);

      // SSID and Schedules sub-features share one flow.
      if (feature.ssidSubFeature || feature.schedulesSubFeature) {
        if (lacksPremiumAccess) {
          setFeatureLevelTimer(index, 'premium');
        } else if (isSsidScopeMatchingPage(feature, currentPath)) {
          openSsidPickerOnCurrentPage(feature);
          onClose?.();
        } else if (isSchedulesScopeMatchingPage(feature, currentPath)) {
          // Already on a Schedules page: stay here, as SSID sub-features do on an SSID page.
          onClose?.();
        } else if (isAtLocationScope()) {
          openScopedFeaturePage(feature, 'network', feature.locationLink || feature.link);
          onClose?.();
        } else if (isAtOrganizationScope(currentPath)) {
          const orgHasLocations = await fetchCurrentOrgHasLocations();
          if (orgHasLocations) {
            openHierarchyTreeForScopedFeature(feature);
          } else {
            openScopedFeaturePage(feature, 'organization', resolveFeatureLink(feature.organizationLink || feature.link));
          }
          onClose?.();
        } else {
          openHierarchyTreeForScopedFeature(feature);
          onClose?.();
        }
        return;
      }

      switch (feature.featureLevel) {
        case 'account':
          // For account level features, check if user has access
          // Restrict premium features for users without token or with restricted roles
          if (lacksPremiumAccess) {
            setFeatureLevelTimer(index, 'premium');
          } else {
            navigateToPath(targetLink, true);
            onClose();
          }
          break;

        case 'organization':
          // For organization level features
          if (lacksPremiumAccess) {
            setFeatureLevelTimer(index, 'premium');
          } else if (!isAtOrganizationScope(currentPath) && !isAtLocationScope()) {
            // At account level (mspHome, etc.) — prompt user to select an organization first.
            // Open the org tree with organization selection mode (similar to location level)
            // Pass the raw link with {orgName} placeholder - it will be resolved after org selection
            window.dispatchEvent(
              new CustomEvent('OPEN_ORG_TREE_FROM_SEARCH', {
                detail: {
                  targetUrl: feature.link,
                  featureTitle: feature.Title,
                  selectionType: 'organization'
                }
              })
            );
            onClose?.();
            setFeatureLevelTimer(index, 'organization');
          } else {
            navigateToPath(targetLink, true);
            onClose();
          }
          break;
        case 'location':
          // For location level features
          if (lacksPremiumAccess) {
            setFeatureLevelTimer(index, 'premium');
          } else if (isOrganizationLevelFeature() || currentPath.includes('organization')) {
            window.dispatchEvent(
              new CustomEvent('OPEN_ORG_TREE_FROM_SEARCH', {
                detail: {
                  targetUrl: targetLink,
                  featureTitle: feature.Title,
                  selectionType: 'location'
                }
              })
            );
            onClose?.();
            setFeatureLevelTimer(index, 'location');
          } else {
            navigateToPath(targetLink, true);
            onClose();
          }
          break;

        default:
          // Default: navigate to feature
          navigateToPath(targetLink, true);
          onClose();
          break;
      }
    },
    [
      isOrganizationLevelFeature,
      setFeatureLevelTimer,
      navigateToPath,
      onClose,
      getUserRole,
      location.pathname,
      openHierarchyTreeForScopedFeature,
      openScopedFeaturePage,
      openSsidPickerOnCurrentPage,
      fetchCurrentOrgHasLocations
    ]
  );

  // Get feature warning message (matching legacy behavior)
  const getFeatureWarningMessage = useCallback(
    (feature) => {
      if (!feature.showWarning) return null;

      const warningType = feature.warningType;

      if (warningType === 'premium') {
        return getLocalizedMessage(intl, 'proFeatureMsg');
      }

      // Location level feature warning
      // if ((warningType === 'location' || !warningType) && feature.featureLevel === "location") {
      //   return getLocalizedMessage(intl, 'locationLevelFeatureWarning');
      // }

      // Organization level feature warning
      if ((warningType === 'organization' || !warningType) && feature.featureLevel === 'organization') {
        return getLocalizedMessage(intl, 'organizationLevelFeatureWarning');
      }

      return null;
    },
    [intl]
  );

  // redirectToDevice function is now imported from utils/CommonMethods.js

  // Handle history click
  const handleHistoryClick = (searchValue) => {
    const sanitizedValue = sanitizeHistorySearchInput(searchValue);
    onSearchChange(sanitizedValue);
  };

  // Handle history delete with optimistic UI update
  const handleHistoryDelete = async (searchValue) => {
    // Capture the item to delete before updating state
    let deletedItem = null;

    // Immediately update UI by removing the item (optimistic update)
    setSearchHistory((prevHistory) => {
      deletedItem = prevHistory.find((item) => item.searchValue === searchValue);
      return prevHistory.filter((item) => item.searchValue !== searchValue);
    });

    try {
      // Make API call in background
      const response = await useApi('DELETE_GLOBAL_SEARCH_HISTORY_ITEM', { searchValue });

      // Check if API call was successful
      if (!response?.response?.status) {
        throw new Error('Delete operation failed');
      }
    } catch (error) {
      console.error('Error deleting search history:', error);
      // Restore the item by adding it back, using the latest state
      if (deletedItem) {
        setSearchHistory((currentHistory) => {
          // Only restore if the item doesn't already exist (avoid duplicates)
          const itemExists = currentHistory.some((item) => item.searchValue === searchValue);
          if (itemExists) return currentHistory;

          // Add the deleted item back to the current history
          return [...currentHistory, deletedItem];
        });
      }
    }
  };

  // Handle clear all history with optimistic UI update
  const handleClearAllHistory = async () => {
    // Capture the history before clearing
    let historySnapshot = null;

    setSearchHistory((prevHistory) => {
      historySnapshot = [...prevHistory];
      // Immediately clear UI (optimistic update)
      return [];
    });

    try {
      // Make API call in background
      const response = await useApi('DELETE_GLOBAL_SEARCH_HISTORY_ITEM', { searchValue: '' });

      // Check if API call was successful
      if (!response?.response?.status) {
        throw new Error('Clear all operation failed');
      }
    } catch (error) {
      console.error('Error clearing search history:', error);
      // Restore the snapshot if API call fails
      if (historySnapshot) {
        setSearchHistory(historySnapshot);
      }
    }
  };

  // Calculate accurate counts based on current state
  const getDeviceCount = () => {
    if (loading.devices || errors.devices || !searchQuery) return 0;
    return deviceResults.length;
  };

  const getClientCount = () => {
    if (loading.clients || errors.clients || !searchQuery) return 0;
    return clientResults.length;
  };

  const getFeatureCount = () => {
    if (loading.features || errors.features || !searchQuery) return 0;
    return Math.min(featureResults.length, FEATURE_SEARCH_MAX_RESULTS);
  };

  const getSsidCount = () => {
    if (loading.ssids || errors.ssids) return 0;
    return ssidResults.length;
  };

  const tabsData = ssidPickerMode
    ? [{ label: getLocalizedMessage(intl, 'ssids'), count: getSsidCount() }]
    : [
        {
          label: getLocalizedMessage(intl, 'all'),
          count: null
        },
        {
          label: getLocalizedMessage(intl, 'devices'),
          count: getDeviceCount()
        },
        {
          label: getLocalizedMessage(intl, 'clients'),
          count: getClientCount()
        },
        {
          label: getLocalizedMessage(intl, 'features'),
          count: getFeatureCount()
        }
      ];

  return (
    <Box sx={{ width: '100%', height: '100%' }}>
      {/* Search input for mobile (only show if not in dropdown mode) */}
      {/* {isMobile && !isDropdown && (
        <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
          <OutlinedInput
            fullWidth
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search (IP, MAC, serial#, name or a feature)"
            startAdornment={
              <InputAdornment position="start">
                <IconSearch size="20px" />
              </InputAdornment>
            }
            size="small"
          />
        </Box>
      )} */}
      {!searchQuery && !ssidPickerMode ? (
        searchHistory && searchHistory.length > 0 ? (
          <SearchHistory
            history={searchHistory}
            onHistoryClick={handleHistoryClick}
            onHistoryDelete={handleHistoryDelete}
            onClearAll={handleClearAllHistory}
          />
        ) : (
          <Box sx={{ p: 2 }}>
            <Typography component="p" variant="caption" color="grey.600">
              {getLocalizedMessage(intl, 'history')}
            </Typography>
            <Typography variant="body1" color="grey.800" sx={{ mt: 2 }}>
              {getLocalizedMessage(intl, 'noRecentSearchHistory')}
            </Typography>
          </Box>
        )
      ) : (
        <>
          {/* Tabs */}
          <Box sx={{ mt: 1 }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                '& .MuiTab-root': {
                  minHeight: 48,
                  textTransform: 'none',
                  fontSize: '0.875rem'
                },
                '& .MuiTabs-list': {
                  padding: '0 20px'
                }
              }}
            >
              {tabsData.map((tab, index) => (
                <Tab
                  key={index}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {tab.label}
                      {tab.count !== null && (
                        <Typography variant="body1" color="grey.600">
                          ({tab.count > 0 ? tab.count : 0})
                        </Typography>
                      )}
                    </Box>
                  }
                />
              ))}
            </Tabs>
          </Box>
          <Box
            id="tabBlock"
            sx={{
              overflow: 'auto',
              maxHeight: 'calc(100vh - 250px)',
              '&::-webkit-scrollbar': { width: 3 }
            }}
          >
            {/* Tab Panels */}
            {ssidPickerMode ? (
              <TabPanel value={activeTab} index={0} padding={0} sx={{ m: 0 }}>
                <Stack spacing={2} sx={{ px: 0.5 }}>
                  <Alert severity="info" sx={{ mx: 0.5 }}>
                    {getLocalizedMessage(intl, 'ssidSearchSelectPrompt')}
                  </Alert>
                  <Box>
                    <Typography component="p" variant="caption" color="grey.600">
                      {getLocalizedMessage(intl, 'ssids')}
                    </Typography>
                    <SearchResults
                      title={getLocalizedMessage(intl, 'ssids')}
                      results={ssidResults}
                      loading={loading.ssids}
                      error={errors.ssids}
                      onItemClick={handleItemClick}
                      searchQuery={searchQuery}
                      type="ssid"
                      expanded={expandedSections.ssid}
                      onToggleExpand={handleToggleExpand}
                      initialDisplayLimit={FEATURE_SEARCH_COLLAPSED_LIMIT}
                    />
                  </Box>
                </Stack>
              </TabPanel>
            ) : (
              <>
                <TabPanel value={activeTab} index={0} padding={0} sx={{ m: 0 }}>
                  {/* All Tab */}
                  <Stack spacing={2} sx={{ px: 0.5 }} id="search-all-tab-panel">
                    {/* Devices */}
                    <Box>
                      <Typography component="p" variant="caption" color="grey.600">
                        {getLocalizedMessage(intl, 'devices')}
                      </Typography>
                      <SearchResults
                        title={getLocalizedMessage(intl, 'devices')}
                        results={deviceResults}
                        loading={loading.devices}
                        error={errors.devices}
                        onItemClick={handleItemClick}
                        searchQuery={searchQuery}
                        type="device"
                        expanded={expandedSections.device}
                        onToggleExpand={handleToggleExpand}
                      />
                    </Box>
                    <Divider />
                    {/* Clients */}
                    <Box>
                      <Typography component="p" variant="caption" color="grey.600">
                        {getLocalizedMessage(intl, 'clients')}
                      </Typography>
                      <SearchResults
                        title={getLocalizedMessage(intl, 'clients')}
                        results={clientResults}
                        loading={loading.clients}
                        error={errors.clients}
                        onItemClick={handleItemClick}
                        searchQuery={searchQuery}
                        type="client"
                        expanded={expandedSections.client}
                        onToggleExpand={handleToggleExpand}
                      />
                    </Box>
                    <Divider />
                    {/* Features */}
                    <Box>
                      <Typography component="p" variant="caption" color="grey.600">
                        {getLocalizedMessage(intl, 'features')}
                      </Typography>
                      {featureResultsTruncated && (
                        <Alert severity="info" sx={{ mt: 1 }}>
                          {getLocalizedMessage(intl, 'tooManyResultsMsg')}
                        </Alert>
                      )}
                      <SearchResults
                        title={getLocalizedMessage(intl, 'features')}
                        results={visibleFeatureResults}
                        loading={loading.features}
                        error={errors.features}
                        onItemClick={handleItemClick}
                        searchQuery={searchQuery}
                        type="feature"
                        expanded={expandedSections.feature}
                        onToggleExpand={handleToggleExpand}
                        getWarningMessage={getFeatureWarningMessage}
                        initialDisplayLimit={FEATURE_SEARCH_COLLAPSED_LIMIT}
                      />
                    </Box>
                  </Stack>
                </TabPanel>

                <TabPanel value={activeTab} index={1}>
                  {/* Devices Tab */}
                  <SearchResults
                    title={getLocalizedMessage(intl, 'devices')}
                    results={deviceResults}
                    loading={loading.devices}
                    error={errors.devices}
                    onItemClick={handleItemClick}
                    searchQuery={searchQuery}
                    type="device"
                    expanded={expandedSections.device}
                    onToggleExpand={handleToggleExpand}
                  />
                </TabPanel>

                <TabPanel value={activeTab} index={2}>
                  {/* Clients Tab */}
                  <SearchResults
                    title={getLocalizedMessage(intl, 'clients')}
                    results={clientResults}
                    loading={loading.clients}
                    error={errors.clients}
                    onItemClick={handleItemClick}
                    searchQuery={searchQuery}
                    type="client"
                    expanded={expandedSections.client}
                    onToggleExpand={handleToggleExpand}
                  />
                </TabPanel>

                <TabPanel value={activeTab} index={3}>
                  {/* Features Tab */}
                  {featureResultsTruncated && (
                    <Alert severity="info" sx={{ mt: 1 }}>
                      {getLocalizedMessage(intl, 'tooManyResultsMsg')}
                    </Alert>
                  )}
                  <SearchResults
                    title={getLocalizedMessage(intl, 'features')}
                    results={visibleFeatureResults}
                    loading={loading.features}
                    error={errors.features}
                    onItemClick={handleItemClick}
                    searchQuery={searchQuery}
                    type="feature"
                    expanded={expandedSections.feature}
                    onToggleExpand={handleToggleExpand}
                    getWarningMessage={getFeatureWarningMessage}
                    initialDisplayLimit={FEATURE_SEARCH_COLLAPSED_LIMIT}
                  />
                </TabPanel>
              </>
            )}
          </Box>
        </>
      )}
    </Box>
  );
};

GlobalSearchComponent.propTypes = {
  searchQuery: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onSearchChange: PropTypes.func.isRequired,
  isMobile: PropTypes.bool,
  isDropdown: PropTypes.bool
};

GlobalSearchComponent.defaultProps = {
  searchQuery: '',
  isMobile: false,
  isDropdown: false
};

export default GlobalSearchComponent;
